// One row per keystroke burst is unreadable, so consecutive saves by the same
// author inside this window fold into the head row instead of stacking.
export const VERSION_MERGE_WINDOW_MS = 10 * 60 * 1000

export interface VersionHead {
	createdAt: number
	createdBySource: string
	createdById?: string | null
}

export function shouldMergeIntoHead({
	head,
	source,
	userId,
	now,
	windowMs = VERSION_MERGE_WINDOW_MS,
	forceNewVersion = false,
}: {
	head: VersionHead | undefined
	source: string
	userId?: string | null
	now: number
	windowMs?: number
	// A restore must never fold into the head row: that row holds the state the
	// restore replaces, which is exactly what the user may want back.
	forceNewVersion?: boolean
}): boolean {
	if (forceNewVersion) return false
	if (!head) return false
	if (head.createdBySource !== source) return false
	if ((head.createdById ?? null) !== (userId ?? null)) return false
	const age = now - head.createdAt
	// A clock skew that puts the head in the future must not merge forever.
	return age >= 0 && age < windowMs
}

// Version rows get pruned at the cap, so numbering from the row count stalls
// there: every later save reports the same number. Derive it from the highest
// number still stored instead.
export function nextVersionNumber(
	versions: readonly { version: number }[],
): number {
	return versions.reduce((max, v) => Math.max(max, v.version), 0) + 1
}

// Marker for a save rejected because the base row moved since the editor
// loaded it. Detected via substring: the message crosses the Zero mutation
// boundary and may arrive wrapped.
export const CHANGELOG_SAVE_CONFLICT = "changelog-save-conflict"

export function isChangelogSaveConflict(message: unknown): boolean {
	return (
		typeof message === "string" && message.includes(CHANGELOG_SAVE_CONFLICT)
	)
}

// Rows to drop, oldest first, so that inserting one more stays within `cap`.
export function versionsToPrune<T extends { createdAt: number }>(
	versions: readonly T[],
	cap: number,
): T[] {
	const overflow = versions.length - cap + 1
	if (overflow <= 0) return []
	return [...versions]
		.sort((a, b) => a.createdAt - b.createdAt)
		.slice(0, overflow)
}
